import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { ArrowBack, ArrowForward, Folder, Home } from 'react-ionicons';
import { connect } from 'react-redux';
import FileExplorerContext from '../../../contexts/FileExplorerContext';
import './MoveElementModal.scss';
import { moveElement } from '../../../api/actions/Documents';
import { toast } from 'react-toastify';

const MoveElementModal = ({ profile, element, show, onClose }) => {
    const {
        fullPath,
        folderArchitecture,
        onOpenFolder,
        fetchFolderArchitecture,
    } = useContext(FileExplorerContext);

    const [selectedFolder, setSelectedFolder] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [folderOptions, setFolderOptions] = useState([]);
    const [previousFolder, setPreviousFolder] = useState([]);

    const parentFolder = useMemo(() => {
        if (fullPath.length === 0) {
            return { id: '', name: 'Home' };
        }

        const lastItem = fullPath[fullPath.length - 1];

        return lastItem;
    }, [fullPath]);

    useEffect(() => {
        if (show && folderArchitecture && folderArchitecture.length > 0) {
            setFolderOptions([folderArchitecture]);
        } else {
            setFolderOptions([]);
            setPreviousFolder([]);
            setSelectedFolder(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const onMove = useCallback(async () => {
        setIsLoading(true);

        try {
            const currentFolder =
                fullPath.length === 0 ? '' : fullPath[fullPath.length - 1].id;

            await moveElement(profile.entityId, element.id, selectedFolder);
            onOpenFolder(currentFolder);

            if (element.isDirectory) {
                fetchFolderArchitecture();
            }

            onClose();
        } catch (error) {
            console.error(error);
            toast.error(
                `Something went wrong while moving the ${
                    element.isDirectory ? 'folder' : 'file'
                }. Please try again.`,
                {
                    className: 'custom-toast',
                }
            );
        } finally {
            setIsLoading(false);
        }
    }, [
        profile,
        selectedFolder,
        element,
        onOpenFolder,
        fullPath,
        fetchFolderArchitecture,
        onClose,
    ]);

    const onFolderClicked = useCallback(
        (folderId) => {
            if (selectedFolder === folderId) {
                setSelectedFolder(undefined);
            } else {
                setSelectedFolder(folderId);
            }
        },
        [selectedFolder]
    );

    const onOpenChildFolder = useCallback(
        (event, folder) => {
            event.stopPropagation();
            event.preventDefault();

            setPreviousFolder([...previousFolder, folder]);
            setFolderOptions([...folderOptions, folder.children]);
            setSelectedFolder(undefined);
        },
        [folderOptions, previousFolder]
    );

    const onPreviousFolderClicked = useCallback(() => {
        const newPreviousFolders = [...previousFolder];
        const newFolderOptions = [...folderOptions];

        newPreviousFolders.pop();
        newFolderOptions.pop();

        setFolderOptions(newFolderOptions);
        setPreviousFolder(newPreviousFolders);
    }, [previousFolder, folderOptions]);

    return (
        <Modal show={show} onHide={onClose}>
            <Modal.Header closeButton>
                Move {element.isDirectory ? 'folder' : 'file'}
            </Modal.Header>
            <Modal.Body>
                <h3>
                    Move{' '}
                    {element.isDirectory ? element.name : element.file.fileName}
                </h3>
                <div className="d-flex align-items-center my-4">
                    <p>Actual location:</p>
                    <div className="p-2 border rounded d-flex align-items-center gap-2 ms-2">
                        <div className="colored-icon">
                            {fullPath.length === 0 ? (
                                <Home color="inherit" />
                            ) : (
                                <Folder color="inherit" />
                            )}
                        </div>
                        <p>{parentFolder.name}</p>
                    </div>
                </div>
                <div>
                    <p>New location:</p>

                    {previousFolder && previousFolder.length > 0 && (
                        <div className="d-flex align-items-center mt-4 mb-4 gap-2">
                            <ArrowBack
                                onClick={onPreviousFolderClicked}
                                className="opacity-75 cursor-pointer"
                            />
                            <p>
                                {previousFolder[previousFolder.length - 1].name}
                            </p>
                        </div>
                    )}

                    <div>
                        {folderOptions &&
                            folderOptions.length > 0 &&
                            folderOptions[folderOptions.length - 1].map(
                                (folder) => {
                                    if (
                                        folder.id === element.id ||
                                        folder.type !== 'folder'
                                    ) {
                                        return null;
                                    }

                                    return (
                                        <div
                                            key={folder.id}
                                            onClick={() =>
                                                onFolderClicked(folder.id)
                                            }
                                            className={`border rounded d-flex align-items-center justify-content-between p-2 mt-2 cursor-pointer ${
                                                selectedFolder === folder.id
                                                    ? 'selected-folder'
                                                    : ''
                                            }`}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="colored-icon">
                                                    <Folder color="inherit" />
                                                </div>
                                                <p>{folder.name}</p>
                                            </div>
                                            {folder.children &&
                                            folder.children.length > 0 ? (
                                                <ArrowForward
                                                    onClick={(e) =>
                                                        onOpenChildFolder(
                                                            e,
                                                            folder
                                                        )
                                                    }
                                                    className="opacity-75 cursor-pointer"
                                                />
                                            ) : (
                                                <div />
                                            )}
                                        </div>
                                    );
                                }
                            )}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button disabled={!selectedFolder} onClick={onMove}>
                    {isLoading ? <Spinner size="sm" /> : 'Move'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const mapStateToProps = (appState) => ({
    profile: appState.userProfile,
});

export default connect(mapStateToProps)(MoveElementModal);
